// Copyright (c) StrangeBee 2024

import { useLocalMessage } from '@Helpers/locale';
import { Alert } from 'antd';
import React, { ReactElement, useState } from 'react';

import './LicenseTrial.scss';

const DISABLED_KIND = 'regular';

const DAY_UNIT = 86400000; // 1 day to millisecond
const SOON_TIME = 30 * DAY_UNIT; // 1 month
const URGENT_TIME = SOON_TIME / 2; // SOON_TIME / 2

export enum ReminderEnum {
    NONE,
    SOON,
    URGENT,
    EXPIRED,
}
interface LicenseTrialProps {
    expirationDate: number;
    plan: string;
    kind: string;
}

const calcReminder = (expirationDate: number): ReminderEnum => {
    const now = Date.now();

    if (expirationDate - now <= URGENT_TIME) {
        return ReminderEnum.URGENT;
    } else if (expirationDate - now <= SOON_TIME) {
        return ReminderEnum.SOON;
    } else if (expirationDate - now < 0) {
        return ReminderEnum.EXPIRED;
    }
    return ReminderEnum.NONE;
};

export const LicenseTrial = ({ expirationDate, plan, kind }: LicenseTrialProps): ReactElement | null => {
    const i = useLocalMessage();
    const [seen, setSeen] = useState(false);

    const start = Date.now();
    const end = expirationDate;
    const diff = end - start;

    const daysFrom = Math.round(diff / DAY_UNIT);
    const unit = daysFrom > 1 ? 'multiple' : 'single';
    const reminder = calcReminder(expirationDate);

    const isRegular = kind.toLowerCase() === DISABLED_KIND;
    const isValid = reminder === ReminderEnum.NONE;
    const isClosable = isRegular && [ReminderEnum.SOON, ReminderEnum.NONE].includes(reminder);

    const message = isRegular ? 'license.reminder.description' : 'license.trial.description';

    return (!isRegular || !isValid) && !seen ? (
        <Alert
            className="license-trial"
            type="error"
            closable={isClosable}
            onClose={(): void => setSeen(true)}
            message={
                <span
                    className="license-trial__description"
                    dangerouslySetInnerHTML={{
                        __html: i(message, {
                            kind: `<b>${kind}</b>`,
                            plan: `<b>${plan}</b>`,
                            momentFrom: i(`license.trial.from.${unit}`, { daysFrom }),
                        }),
                    }}
                />
            }
        />
    ) : null;
};
